import { useCallback, useEffect, useState } from 'react'
import { webApi } from '@/services/webApi'
import { useCityStore } from '@/store/cityStore'

type PermissionState = 'idle' | 'prompt' | 'granted' | 'denied' | 'unsupported'

const ASKED_KEY = 'qbc-geo-asked'

function titleCase(value: string) {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function markGeoAsked() {
  try {
    window.localStorage.setItem(ASKED_KEY, '1')
  } catch {
    /* private mode — ignore */
  }
}

function wasGeoAsked() {
  try {
    return window.localStorage.getItem(ASKED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Browser geolocation + reverse-geocoding against our own data.
 *
 * - On first visit, prompts for location (unless already granted/denied).
 * - If allowed, auto-selects the nearest city in the header.
 * - If denied or detection fails, falls back to Rajkot.
 * - A past manual city selection is never overridden.
 */
export function useGeolocation(options?: { autoPrompt?: boolean }) {
  const [status, setStatus] = useState<PermissionState>('idle')
  const [error, setError] = useState<string | null>(null)
  const setDetectedCity = useCityStore((s) => s.setDetectedCity)
  const setLocationCity = useCityStore((s) => s.setLocationCity)
  const setDefaultCity = useCityStore((s) => s.setDefaultCity)
  const manual = useCityStore((s) => s.manual)

  const detectPosition = useCallback(
    (markAsked = false, forceManualOverride = false) => {
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        setStatus('unsupported')
        setDefaultCity()
        return
      }

      if (markAsked) markGeoAsked()

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          setStatus('granted')
          setError(null)
          try {
            const detected = await webApi.reverseGeocode({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            })
            if (detected?.cityId && detected.city) {
              const cityName = titleCase(detected.city)
              if (forceManualOverride) {
                setLocationCity(detected.cityId, cityName)
              } else {
                setDetectedCity(detected.cityId, cityName)
              }
            } else {
              setDefaultCity()
            }
          } catch {
            setDefaultCity()
          }
        },
        (err) => {
          setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'idle')
          setError(err.message)
          setDefaultCity()
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60_000 },
      )
    },
    [setDefaultCity, setDetectedCity, setLocationCity],
  )

  const request = useCallback(() => {
    setStatus('prompt')
    // User explicitly tapped "use my location" — always apply detected city.
    detectPosition(true, true)
  }, [detectPosition])

  useEffect(() => {
    if (!options?.autoPrompt || manual) return

    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    async function init() {
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        setStatus('unsupported')
        setDefaultCity()
        return
      }

      try {
        const perm = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        if (cancelled) return

        if (perm.state === 'denied') {
          setStatus('denied')
          setDefaultCity()
          return
        }

        if (perm.state === 'granted') {
          detectPosition()
          return
        }
      } catch {
        /* Permissions API unavailable — fall through to prompt flow */
      }

      if (!wasGeoAsked()) {
        timeoutId = window.setTimeout(() => {
          if (!cancelled) request()
        }, 1200)
        return
      }

      detectPosition()
    }

    void init()

    return () => {
      cancelled = true
      if (timeoutId) window.clearTimeout(timeoutId)
    }
  }, [options?.autoPrompt, manual, detectPosition, request, setDefaultCity])

  return { status, error, request }
}
